import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { FileText, Download, Eye, History, Loader2 } from "lucide-react"
import { paymentBase } from "@/lib/collectionLedger"
import { api } from "@/lib/api"
import { formatDate } from "@/lib/format"
import { toast } from "sonner"
import CollectionReceiptPreviewModal from "./CollectionReceiptPreviewModal"

export default function CollectionReceiptsHistoryModal({
  collection,
  open,
  onOpenChange,
  isMobile = false,
  onReceiptUpdated,
}) {
  const [receipts, setReceipts] = useState([])
  const [loading, setLoading] = useState(false)
  const [previewReceipt, setPreviewReceipt] = useState(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [downloadingId, setDownloadingId] = useState(null)

  const fetchReceipts = async () => {
    if (!collection?.id) return
    setLoading(true)
    try {
      const res = await api.get(`${paymentBase(collection)}/receipts`)
      setReceipts(res.data)
    } catch (err) {
      console.error("Failed to fetch receipts history", err)
      toast.error("Makbuz geçmişi yüklenemedi.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && collection?.id) {
      fetchReceipts()
    }
    // eslint-disable-next-line
  }, [open, collection?.id])

  const handleDownload = async (r) => {
    try {
      setDownloadingId(r.id)
      const res = await api.get(`/collection-receipts/${r.id}/pdf`, {
        params: { download: true },
        responseType: "blob",
      })
      const url = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      )
      const a = document.createElement("a")
      a.href = url
      a.download = `Tahsilat_Makbuzu_${r.receipt_number || "TM"}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (err) {
      toast.error("Makbuz indirilemedi.")
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[85vh] flex flex-col p-6 bg-white border border-[#DCE2E8] shadow-xl">
          <DialogHeader className="shrink-0 pb-3 border-b border-[#DCE2E8]">
            <div className="flex items-center gap-2 text-slate-900">
              <History className="h-5 w-5 text-blue-600 shrink-0" />
              <DialogTitle className="text-base font-semibold">
                Makbuz Geçmişi
              </DialogTitle>
            </div>
            <DialogDescription className="text-xs text-slate-500 pt-1">
              Bu tahsilata ait düzenlenmiş ve iptal edilmiş tüm resmi makbuz kayıtları.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto py-2">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500 text-xs">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600 mb-2" />
                Makbuzlar yükleniyor...
              </div>
            ) : receipts.length === 0 ? (
              <div className="text-center py-10 text-xs text-slate-500">
                Bu tahsilata ait henüz bir makbuz kaydı bulunmamaktadır.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Makbuz No</TableHead>
                    <TableHead className="text-xs">Durum</TableHead>
                    <TableHead className="text-xs">Düzenlenme</TableHead>
                    <TableHead className="text-xs">İptal Nedeni</TableHead>
                    <TableHead className="text-right text-xs">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receipts.map((r) => {
                    const isVoided = r.status === "voided"
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium text-xs text-slate-900">
                          {r.receipt_number}
                        </TableCell>
                        <TableCell>
                          {isVoided ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-red-100 text-red-700 border border-red-200">
                              İptal
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-green-100 text-green-700 border border-green-200">
                              Aktif
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">
                          {formatDate(r.issued_at)}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 max-w-[160px] truncate">
                          {isVoided ? r.void_reason || "-" : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs border-slate-200 text-slate-700 hover:bg-slate-50"
                              onClick={() => {
                                setPreviewReceipt(r)
                                setPreviewOpen(true)
                              }}
                              title="Makbuzu Görüntüle"
                              data-testid={`preview-history-receipt-${r.id}`}
                            >
                              <Eye className="w-3.5 h-3.5 mr-1 text-slate-500" />
                              Görüntüle
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-blue-600 hover:bg-blue-50"
                              onClick={() => handleDownload(r)}
                              disabled={downloadingId === r.id}
                              title="PDF İndir"
                              data-testid={`download-history-receipt-${r.id}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </div>

          <DialogFooter className="shrink-0 pt-3 border-t border-[#DCE2E8]">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="text-xs h-9 border-[#DCE2E8]"
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embedded preview modal for selected historical receipt */}
      {previewOpen && previewReceipt && (
        <CollectionReceiptPreviewModal
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          receipt={previewReceipt}
          isMobile={isMobile}
          onReceiptVoided={(updated) => {
            setPreviewReceipt(updated)
            fetchReceipts()
            if (onReceiptUpdated) {
              onReceiptUpdated()
            }
          }}
        />
      )}
    </>
  )
}
